import './style.css';
import type { GameState } from './types';
import { loadGameState, saveGameState, createNewState, calculatePassiveRate, calculateClickPower, calculateOfflineProgress } from './game-state';
import { PROCRASTINATIONS, DETOURS, WISDOM_UPGRADES, getProcrastinationCost, getDetourCost, getWisdomUpgradeCost } from './upgrades';
import { performExistentialEpiphany } from './prestige';
import { initDunes } from './dunes';
import { 
  renderUI, 
  setupTabs, 
  addLogEntry, 
  spawnFloatingClickText, 
  drawHourglassSand, 
  updateHourglassSandParticles, 
  showOfflineModal, 
  setupOfflineModalClose,
  initMuteButtonState,
  toggleSound,
  playTickSound,
  playClickSound,
  playUpgradeSound,
  playPrestigeSound,
  showEndGameModal,
  hideEndGameModal
} from './ui';

let state: GameState;
let lastTickTime = Date.now();
let lastSaveTime = Date.now();
let lastLogTime = Date.now();
let autoFlipTimer = 0;

const RANDOM_LOGS = [
  "You blinked. That wasted 150 milliseconds.",
  "You adjusted your sitting posture. Ah, much better.",
  "You wondered if cats think in languages.",
  "Stared at the browser tab icon for a solid 8 seconds.",
  "Thought about that embarrassing thing you did in 2012.",
  "You checked the time on your phone, locked it, then checked it again.",
  "Wondered what the temperature is in Oslo right now.",
  "You spun your pen. It fell. You spent 10 seconds picking it up.",
  "Thought about cleaning your desk, but decided against it.",
  "You double-clicked a random word on this page for no reason.",
  "Doodled a tiny spiral on an imaginary notepad.",
  "Cleaned a speck of dust off your screen.",
  "You sighed. Entropy increases.",
  "Wondered if anyone else is wasting time at this exact moment."
];

// Initialize Game
function init() {
  state = loadGameState();
  
  // Set initial hourglass rotation based on loaded state without transition
  const svg = document.getElementById('hourglass-svg');
  if (svg) {
    svg.style.transition = 'none';
    svg.style.transform = `rotate(${state.rotationDegrees}deg)`;
    svg.offsetHeight; // trigger reflow
    svg.style.transition = '';
  }
  
  // Initialize physics sand dunes background
  initDunes();
  
  // Apply settings state
  initMuteButtonState();
  
  // Set up Tabs
  setupTabs();
  setupOfflineModalClose();
  
  // Check for End Game condition on load
  if (state.gameCompleted && !state.hasSeenEndScreen) {
    showEndGameModal(state);
  }
  
  // Check Offline Progress
  const offline = calculateOfflineProgress(state);
  if (offline) {
    const { elapsedSeconds, wastedOffline } = offline;
    
    // Add offline progress to state
    state.timeWasted += wastedOffline;
    state.totalTimeWasted += wastedOffline;
    state.lifetimeTimeWasted += wastedOffline;
    state.stats.totalOfflineTimeWasted += wastedOffline;
    
    addLogEntry(`You wasted ${wastedOffline.toFixed(1)}s doing absolutely nothing while away for ${elapsedSeconds} seconds.`, 'offline');
    showOfflineModal(elapsedSeconds, wastedOffline);
    saveGameState(state);
  } else {
    addLogEntry("Welcome back. Time is passing. Do not panic.", "default");
  }
  
  // Set up Event Listeners
  setupEventListeners();
  
  // Start Game Loops
  lastTickTime = Date.now();
  requestAnimationFrame(renderLoop);
  setInterval(logicTick, 50); // 20Hz Logic Tick
}

function setupEventListeners() {
  // Sound Toggle
  const soundBtn = document.getElementById('sound-toggle');
  if (soundBtn) {
    soundBtn.addEventListener('click', () => {
      toggleSound();
    });
  }
  
  // Hard Reset Game
  const resetBtn = document.getElementById('reset-button');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      const confirmReset = confirm("Are you absolutely sure you want to reset all progress? This will delete your time wasted, prestige count, and wisdom upgrades permanently!");
      if (confirmReset) {
        state = createNewState();
        saveGameState(state);
        // Reset rotation in DOM without transition
        const svg = document.getElementById('hourglass-svg');
        if (svg) {
          svg.style.transition = 'none';
          svg.style.transform = `rotate(${state.rotationDegrees}deg)`;
          svg.offsetHeight; // trigger reflow
          svg.style.transition = '';
        }
        addLogEntry("Reality reset. All wasted time has been erased.", "default");
        renderUI(state);
      }
    });
  }
  
  // Hourglass Click / Flip
  const hourglass = document.getElementById('hourglass-click-target');
  const svg = document.getElementById('hourglass-svg');
  if (hourglass && svg) {
    hourglass.addEventListener('click', (e: MouseEvent) => {
      const clickPower = calculateClickPower(state);
      
      // If sand is empty, click flips the hourglass
      if (state.sandAmount <= 0) {
        if (svg.classList.contains('flipping')) return;
        
        svg.classList.add('flipping');
        state.rotationDegrees += 180;
        svg.style.transform = `rotate(${state.rotationDegrees}deg)`;
        
        playClickSound();
        addLogEntry("You flipped the hourglass to let time flow again.", "default");
        
        // Spawn small floating text
        spawnFloatingClickText(0, e.clientX, e.clientY);
        
        setTimeout(() => {
          svg.classList.remove('flipping');
          state.sandAmount = state.maxSandCapacity;
          renderUI(state);
          saveGameState(state);
        }, 700);
      } else {
        // Normal click behavior: waste time manually
        state.timeWasted += clickPower;
        state.totalTimeWasted += clickPower;
        state.lifetimeTimeWasted += clickPower;
        state.clicks++;
        
        // Brief visual squeeze click effect on the container (avoiding inline transform conflicts)
        hourglass.classList.add('clicking');
        setTimeout(() => hourglass.classList.remove('clicking'), 100);
        
        playClickSound();
        spawnFloatingClickText(clickPower, e.clientX, e.clientY);
      }
      renderUI(state);
    });
  }
  
  // Prestige Trigger
  const prestigeBtn = document.getElementById('prestige-trigger-button');
  if (prestigeBtn) {
    prestigeBtn.addEventListener('click', () => {
      const gain = state.totalTimeWasted;
      if (gain >= 3600) {
        const confirmPrestige = confirm("Are you ready to experience an Existential Epiphany? This will reset your current time, procrastinations, and click detours, but you will receive Wisdom points to spend on powerful permanent upgrades.");
        if (confirmPrestige) {
          state = performExistentialEpiphany(state);
          playPrestigeSound();
          addLogEntry(`You achieved an Existential Epiphany! You converted your wasted life into Wisdom.`, 'prestige');
          renderUI(state);
        }
      }
    });
  }
  
  // Shop Upgrades Buy click handler (Event Delegation)
  // 1. Procrastinations
  const procList = document.getElementById('procrastinations-list');
  if (procList) {
    procList.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('.btn-buy');
      if (!btn) return;
      
      const id = btn.getAttribute('data-id');
      if (!id) return;
      
      const p = PROCRASTINATIONS.find(x => x.id === id);
      if (p) {
        const count = state.procrastinations[id] || 0;
        const costReduction = state.wisdomUpgrades['idle_stupor'] || 0;
        const cost = getProcrastinationCost(p, count, costReduction);
        
        if (state.timeWasted >= cost) {
          state.timeWasted -= cost;
          state.procrastinations[id] = count + 1;
          playUpgradeSound();
          addLogEntry(`Automated Procrastination: Unlocked level ${count + 1} of "${p.name}".`, 'upgrade');
          renderUI(state);
        }
      }
    });
  }
  
  // 2. Click Detours
  const detourList = document.getElementById('detours-list');
  if (detourList) {
    detourList.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('.btn-buy-detour');
      if (!btn) return;
      
      const id = btn.getAttribute('data-id');
      if (!id) return;
      
      const d = DETOURS.find(x => x.id === id);
      if (d) {
        const count = state.detours[id] || 0;
        const cost = getDetourCost(d, count);
        
        if (state.timeWasted >= cost) {
          state.timeWasted -= cost;
          state.detours[id] = count + 1;
          playUpgradeSound();
          addLogEntry(`Focus Detour: Purchased "${d.name}" (x${count + 1}).`, 'upgrade');
          renderUI(state);
        }
      }
    });
  }
  
  // 3. Wisdom Upgrades
  const wisdomList = document.getElementById('wisdom-list');
  if (wisdomList) {
    wisdomList.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('.btn-buy-wisdom');
      if (!btn) return;
      
      const id = btn.getAttribute('data-id');
      if (!id) return;
      
      const w = WISDOM_UPGRADES.find(x => x.id === id);
      if (w) {
        const lvl = state.wisdomUpgrades[id] || 0;
        const cost = getWisdomUpgradeCost(w, lvl);
        
        if (state.wisdom >= cost && lvl < w.maxLevel) {
          state.wisdom -= cost;
          state.wisdomUpgrades[id] = lvl + 1;
          playUpgradeSound();
          addLogEntry(`Wisdom Upgrade: Unlocked Level ${lvl + 1} of "${w.name}".`, 'upgrade');
          
          // Check if all upgrades are maxed
          const allMaxed = WISDOM_UPGRADES.every(x => (state.wisdomUpgrades[x.id] || 0) >= x.maxLevel);
          if (allMaxed && !state.gameCompleted) {
            state.gameCompleted = true;
            state.hasSeenEndScreen = false;
            showEndGameModal(state);
            addLogEntry("CONGRATULATIONS! You have maxed all upgrades. Time is fully wasted.", "prestige");
            playPrestigeSound();
          }
          
          renderUI(state);
          saveGameState(state);
        }
      }
    });
  }
  
  // End Game Modal controls
  const endContinueBtn = document.getElementById('end-continue-btn');
  if (endContinueBtn) {
    endContinueBtn.addEventListener('click', () => {
      state.hasSeenEndScreen = true;
      saveGameState(state);
      hideEndGameModal();
    });
  }
  
  const endResetBtn = document.getElementById('end-reset-btn');
  if (endResetBtn) {
    endResetBtn.addEventListener('click', () => {
      const confirmReset = confirm("Are you ready to erase all existence and restart from scratch?");
      if (confirmReset) {
        state = createNewState();
        saveGameState(state);
        hideEndGameModal();
        // Reset rotation in DOM without transition
        const svg = document.getElementById('hourglass-svg');
        if (svg) {
          svg.style.transition = 'none';
          svg.style.transform = `rotate(${state.rotationDegrees}deg)`;
          svg.offsetHeight; // trigger reflow
          svg.style.transition = '';
        }
        addLogEntry("Reality has been erased and reconstructed. Begin anew.", "default");
        renderUI(state);
      }
    });
  }
}

// Logical Update Loop (20Hz)
function logicTick() {
  const now = Date.now();
  let deltaTime = (now - lastTickTime) / 1000;
  lastTickTime = now;
  
  // Cap delta time to prevent giant leaps in calculation during browser stuttering
  if (deltaTime > 2.0) deltaTime = 2.0;
  
  const passiveRate = calculatePassiveRate(state);
  
  // 1. Passive Time Accumulation and Sand Depletion
  if (state.sandAmount > 0) {
    state.timeWasted += passiveRate * deltaTime;
    state.totalTimeWasted += passiveRate * deltaTime;
    state.lifetimeTimeWasted += passiveRate * deltaTime;
    
    // Sand runs out based on passive flow rate or click drain
    state.sandAmount = Math.max(0, state.sandAmount - deltaTime);
  } else {
    // 10% passive generation when sand is empty
    state.timeWasted += passiveRate * 0.10 * deltaTime;
    state.totalTimeWasted += passiveRate * 0.10 * deltaTime;
    state.lifetimeTimeWasted += passiveRate * 0.10 * deltaTime;
  }
  
  // 2. Auto-Sighing Wisdom Upgrade (clicks automatically)
  const autoSigherLvl = state.wisdomUpgrades['auto_sigher'] || 0;
  if (autoSigherLvl > 0) {
    const clicksPerSec = autoSigherLvl;
    const clickPower = calculateClickPower(state);
    
    // Autoclicks generate time
    const autoClickTime = clickPower * clicksPerSec * deltaTime;
    state.timeWasted += autoClickTime;
    state.totalTimeWasted += autoClickTime;
    state.lifetimeTimeWasted += autoClickTime;
    
    // Auto-flipper mechanism: If sand is empty and they have auto-sigher, 
    // it automatically flips the hourglass after a 6-second delay.
    if (state.sandAmount <= 0) {
      autoFlipTimer += deltaTime;
      if (autoFlipTimer >= 6.0) {
        autoFlipTimer = 0;
        const svg = document.getElementById('hourglass-svg');
        if (svg && !svg.classList.contains('flipping')) {
          svg.classList.add('flipping');
          state.rotationDegrees += 180;
          svg.style.transform = `rotate(${state.rotationDegrees}deg)`;
          
          playTickSound();
          addLogEntry("Your subconscious automated sighing flipped the hourglass.", "default");
          
          setTimeout(() => {
            if (svg) {
              svg.classList.remove('flipping');
            }
            state.sandAmount = state.maxSandCapacity;
            renderUI(state);
            saveGameState(state);
          }, 700);
        }
      }
    }
  } else {
    autoFlipTimer = 0;
  }
  
  // 3. Stats update
  state.stats.timeSpentActive += deltaTime;
  if (passiveRate > state.stats.maxTimeWastedPassiveRate) {
    state.stats.maxTimeWastedPassiveRate = passiveRate;
  }
  
  // 4. Random Humor Logs
  if (now - lastLogTime > 30000 + Math.random() * 20000) {
    lastLogTime = now;
    const randomLog = RANDOM_LOGS[Math.floor(Math.random() * RANDOM_LOGS.length)];
    addLogEntry(randomLog, 'default');
    playTickSound();
  }
  
  // 5. Periodic Auto-Save (every 10 seconds)
  if (now - lastSaveTime > 10000) {
    lastSaveTime = now;
    saveGameState(state);
    
    // Briefly animate the saving indicator in UI console
    const indicator = document.getElementById('autosave-indicator');
    if (indicator) {
      indicator.textContent = 'saved!';
      indicator.style.opacity = '1';
      setTimeout(() => {
        if (indicator) {
          indicator.textContent = 'auto-saving...';
          indicator.style.opacity = '0.4';
        }
      }, 1500);
    }
  }
}

// 60FPS Render loop for canvas/SVG sand particles and high frequency UI updates
let lastFrameTime = Date.now();
function renderLoop() {
  const now = Date.now();
  const deltaSec = (now - lastFrameTime) / 1000;
  lastFrameTime = now;
  
  if (state) {
    // 1. Draw sand levels in hourglass
    const sandRatio = state.sandAmount / state.maxSandCapacity;
    
    // Check if the hourglass is currently mid-flip transition
    const svg = document.getElementById('hourglass-svg');
    const isCurrentlyFlipping = svg ? svg.classList.contains('flipping') : false;
    
    // If transitioning, draw sand based on the PREVIOUS orientation degrees
    const effectiveDegrees = isCurrentlyFlipping 
      ? (state.rotationDegrees - 180) 
      : state.rotationDegrees;
      
    const isFlipped = Math.round(effectiveDegrees / 180) % 2 !== 0;
    
    drawHourglassSand(sandRatio, isFlipped);
    
    // 2. Animate falling sand stream and particles
    const passiveRate = calculatePassiveRate(state);
    const isFlowing = state.sandAmount > 0 && passiveRate > 0;
    updateHourglassSandParticles(sandRatio, isFlowing, deltaSec, isFlipped);
    
    // 3. Update main numerical counters and tabs shop states
    renderUI(state);
  }
  
  requestAnimationFrame(renderLoop);
}

// Start Game on load
window.addEventListener('load', () => {
  init();
});

// Developer Testing Hook: Expose completion trigger in window console
(window as any).triggerGameEnd = () => {
  WISDOM_UPGRADES.forEach(wu => {
    state.wisdomUpgrades[wu.id] = wu.maxLevel;
  });
  state.gameCompleted = true;
  state.hasSeenEndScreen = false;
  
  saveGameState(state);
  showEndGameModal(state);
  playPrestigeSound();
  renderUI(state);
  
  console.log("Cheat active: Maxed all Wisdom upgrades! End of game screen triggered.");
};
